import { Component, OnInit, inject } from '@angular/core';
import {TranslationService} from '../../../core/services/translation.service';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import {HupSubscription} from '../../../data/models/subscription.model';
import {Payment} from '../../../data/models/payment.model';
import {ActivatedRoute} from '@angular/router';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {PaymentService} from '../../../data/services/payment.service';
import {ErrorService} from '../../../core/components/general-errors/error.service';
import {BaseDataService} from '../../../core/services/base-data.service';
import {CustomValidators} from '../../../core/validators';
import {BaseData} from '../../../data/models/base-data.model';
import {NavigationCommand, NavigationService} from "../../../data/services/navigation.service";
import {FormOfPayment} from "../../../data/models/formOfPayment.model";
import {DateAdapter, MatOption} from '@angular/material/core';

import {LoadingSpinnerComponent} from "../../../core/components/loading-spinner/loading-spinner.component";
import {GeneralErrorsComponent} from "../../../core/components/general-errors/general-errors.component";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatSelect} from "@angular/material/select";
import {MatButton} from "@angular/material/button";
import {MatCard, MatCardContent} from "@angular/material/card";
import {ApplyIfNotEmptyDirective} from "../../../core/directives/apply-if-not-empty.directive";
import {ConfigService} from "../../../core/services/config.service";
import {BankAccountPipe} from "../../../core/pipes/bank-account.pipe";
import {forkJoin, of} from "rxjs";
import {catchError} from "rxjs/operators";

@Component({
  selector: 'app-subscription-payment-edit',
  templateUrl: './subscription-payment-edit.component.html',
  styleUrls: ['./subscription-payment-edit.component.scss'],
  imports: [
    LoadingSpinnerComponent,
    GeneralErrorsComponent,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatButton,
    MatLabel,
    MatError,
    MatCard,
    MatCardContent,
    ApplyIfNotEmptyDirective,
    BankAccountPipe
  ],
  standalone: true
})
export class SubscriptionPaymentEditComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private hupSubscriptionsService = inject(HupSubscriptionsService);
  private configService = inject(ConfigService);
  private paymentService = inject(PaymentService);
  private errorService = inject(ErrorService);
  private baseDataService = inject(BaseDataService);
  translationService = inject(TranslationService);
  private navigationService = inject(NavigationService);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);

  paymentEditForm: FormGroup;
  sub: HupSubscription;
  rawIban: string;
  isLoadingSub: boolean = false;
  errorsArr: string[] = [];
  isMobile: boolean = false;
  minDate: Date = new Date();

  formsOfPayment: BaseData[] = [];
  paymentFrequencies: BaseData[] = [];
  isSaving: boolean = false;

  filteredFormsOfPayment: FormOfPayment[] = [];
  filteredPaymentFrequencies: BaseData[] = [];
  needIbanBic: boolean = false;
  needInvoiceMailAddress: boolean = false;

  /** Aktive Bankverbindungen des angemeldeten Nutzers (aus /webabo/bank). */
  bankConnections: Payment[] = [];
  /** Auswahlliste für das Bankverbindungs-Dropdown (Nutzer-Bankverbindungen inkl. der aktuell am Abo hinterlegten). */
  availableBankConnections: Payment[] = [];
  /** true, wenn für das Abo keine Zahlart/Kein Zahlrhythmus wählbar ist -> Hinweis statt Formular. */
  changeNotPossible: boolean = false;

  constructor() {
    this.dateAdapter.getFirstDayOfWeek = () => 1;
    this.paymentFrequencies = this.baseDataService.getBaseData('invoicemethods');
    this.formsOfPayment = this.baseDataService.getBaseData('paymentmethods');
  }

  ngOnInit(): void {
    this.isLoadingSub = true;

    let configValueBoolean = this.configService.getConfig('payment.changeOfPaymentConditionsAllowed')?.asBoolean();
    if(!configValueBoolean) {
      this.navigationService.navigateTo(NavigationCommand.PAYMENT);
    }

    this.paymentEditForm = this.formBuilder.group({
      backendId: [''],
      bank: this.formBuilder.group({
        bankName: [''],
        subId: [''], // ist die nicht falsch verschachtelt?
        swiftBIC: [{value: this.translationService.getTranslation('filled_automatically'), disabled: true}],
        swiftIBAN: [{value: this.translationService.getTranslation('filled_automatically'), disabled: true}],
        webId: [''],
      }),
      invoiceEmail: ['', [Validators.email, Validators.required]],
      paymentType: this.formBuilder.group({
        paymentFrequency: ['', Validators.required],
        paymentMethod: ['', Validators.required],
      }),
      validDate: this.formBuilder.group({
        validFrom: [this.minDate, Validators.required],
        validUntil: [''],
      }),
      webId: [''],
      bankSelection: [''],
    });
    this.setAsyncValidators();

    this.paymentEditForm.get('paymentType.paymentMethod').valueChanges.subscribe(value => {
      if (value !== undefined) {
        this.handleFormOfPaymentChange(value?.paymentMethod ?? value);
      }
    });

    this.route.paramMap.subscribe((params) => {
      forkJoin({
        sub: this.hupSubscriptionsService.getSubscription(params.get('subWebId'), params.get('subBackendId')),
        paymentOptions: this.hupSubscriptionsService.getAllowedPaymentsForSub(+params.get('subBackendId')),
        bankConnections: this.paymentService.getPayments().pipe(catchError((err) => {
          // Fällt auf "keine Bankverbindungen bekannt" zurück (Bankeinzug wird dann nur bei vorhandener
          // Abo-IBAN angeboten) - der restliche Dialog bleibt bedienbar.
          console.error('Bankverbindungen des Nutzers konnten nicht geladen werden', err);
          return of([]);
        }))
      }).subscribe({
        next: res => {
          this.sub = res.sub;
          this.isLoadingSub = false;
          this.rawIban = res.sub?.curPayment?.bank?.swiftIBAN;
          // Aktive Bankverbindungen des Nutzers (nur Sätze mit IBAN, keine Kreditkarten), dedupliziert
          // nach IBAN (das Backend liefert teils Mehrfach-/Dubletten-Sätze mit backendId 0).
          this.bankConnections = this.dedupeByIban(res.bankConnections ?? []);
          this.buildAvailableBankConnections(res.sub);
          this.paymentEditForm.get('bank.swiftBIC').patchValue(res.sub?.curPayment?.bank?.swiftBIC);
          this.paymentEditForm.get('invoiceEmail').patchValue(res.sub?.curPayment?.invoiceEmail);
          this.paymentEditForm.get('bank.subId').patchValue(res.sub?.backendId);
          this.paymentEditForm.get('backendId').patchValue(res.sub?.curPayment?.backendId);
          this.handleFormOfPayment(res.paymentOptions ?? []);
          const foundMethod = this.filteredFormsOfPayment.find(method => method.paymentMethod === res.sub?.curPayment?.paymentType?.paymentMethod);
          this.paymentEditForm.get('paymentType.paymentMethod').patchValue(foundMethod);
          const foundFrequency = this.paymentFrequencies.find(freq => freq.key === res.sub?.curPayment?.paymentType?.paymentFrequency);
          this.paymentEditForm.get('paymentType.paymentFrequency').patchValue(foundFrequency);
        }, error: (error) => {
          this.isLoadingSub = false;
          this.errorsArr.push(this.translationService.getTranslation('error_subscription_init'));
        }
      });
    });

  }

  private setAsyncValidators() {
    this.paymentEditForm.get('bank.swiftIBAN').setAsyncValidators(CustomValidators.validateIBAN(
      this.http,
      this.translationService,
      this.paymentEditForm.get('bank.swiftBIC'),
      this.paymentEditForm.get('bank.bankName')
    ));
  }

  handleFormOfPayment(allowedPayments : FormOfPayment[]): void {
    let possiblePayments : FormOfPayment[] = [];
    if(allowedPayments.length > 0) {
      possiblePayments = allowedPayments;
    } else {
      possiblePayments = this.sub?.offer?.offerPaymentOfferType;
    }

    possiblePayments?.forEach(formOfPayment => {
      const foundFormOfPayment: BaseData = this.formsOfPayment.find(searchFormOfPayment => searchFormOfPayment.key === formOfPayment.paymentMethod);
      if (
          foundFormOfPayment && foundFormOfPayment.useInFE && formOfPayment.paymentFrequencyList?.length > 0 &&
          // Bankeinzug ist auch dann wählbar, wenn zwar dieses Abo keine IBAN hat, der Nutzer aber
          // (mind.) eine aktive Bankverbindung besitzt (siehe Bank-Dropdown).
          ( formOfPayment.paymentType !== 'DIRECTDEBITIBAN' || this.rawIban || this.bankConnections.length > 0 )
      ) {
        formOfPayment.description = foundFormOfPayment.description;
        this.filteredFormsOfPayment.push(formOfPayment);
      }
    });

    // Keine wählbare Zahlart -> Hinweis statt (leerem) Formular anzeigen.
    this.changeNotPossible = this.filteredFormsOfPayment.length === 0;

    if (this.filteredFormsOfPayment.length === 1) {
      // Wert Setzen!
      this.paymentEditForm.get('paymentType.paymentMethod').patchValue(this.filteredFormsOfPayment[0]);
      this.handleFormOfPaymentChange(this.filteredFormsOfPayment[0].paymentMethod);
    }

  }

  handleFormOfPaymentChange(value: string): void {
    this.filteredPaymentFrequencies = [];
    const findFormOfPayment = this.filteredFormsOfPayment?.find(formOfPayment => formOfPayment.paymentMethod === value);

    this.setValidatorsAtIBAN(findFormOfPayment?.paymentType === 'DIRECTDEBITIBAN');
    this.setValidatorsAtInvoiceEmail(findFormOfPayment?.invoiceType === 'EMAILDELIVERY');

    findFormOfPayment?.paymentFrequencyList?.forEach(searchFrequency => {
      const foundFrequency = this.paymentFrequencies.find(frequency => frequency.key === searchFrequency);
      if (foundFrequency) {
        this.filteredPaymentFrequencies.push(foundFrequency);
      }
    });
    if (this.filteredPaymentFrequencies.length === 1) {
      this.paymentEditForm.get('paymentType.paymentFrequency').patchValue(this.filteredPaymentFrequencies[0]);
    }
  }


  setValidatorsAtIBAN(value: boolean) {
    if (value === true) {
      this.needIbanBic = true;
      // Auswahl einer (vorhandenen) Bankverbindung ist Pflicht.
      this.paymentEditForm.get('bankSelection').setValidators(Validators.required);
      this.paymentEditForm.get('bankSelection').updateValueAndValidity({emitEvent: false});
      this.prepareBankSelection();
    } else {
      this.needIbanBic = false;
      this.paymentEditForm.get('bankSelection').clearValidators();
      this.paymentEditForm.get('bankSelection').setValue('', {emitEvent: false});
      this.paymentEditForm.get('bankSelection').updateValueAndValidity({emitEvent: false});

      this.paymentEditForm.get('bank').reset();
    }
  }

  /** Entfernt Sätze ohne IBAN sowie IBAN-Dubletten (Identität einer Bankverbindung ist hier die IBAN). */
  private dedupeByIban(connections: Payment[]): Payment[] {
    const seenIbans = new Set<string>();
    return connections.filter(connection => {
      const iban = connection?.bank?.swiftIBAN;
      if (!iban || seenIbans.has(iban)) {
        return false;
      }
      seenIbans.add(iban);
      return true;
    });
  }

  /**
   * Baut die Auswahlliste des Bank-Dropdowns: die aktiven Bankverbindungen des Nutzers, ergänzt um die
   * aktuell am Abo hinterlegte Bankverbindung (falls diese nicht ohnehin schon enthalten ist).
   */
  private buildAvailableBankConnections(sub: HupSubscription): void {
    const connections: Payment[] = [...this.bankConnections];
    const currentBank = sub?.curPayment?.bank;
    if (currentBank?.swiftIBAN && !connections.some(c => c.bank?.swiftIBAN === currentBank.swiftIBAN)) {
      connections.push({
        webId: sub.curPayment.webId,
        backendId: currentBank.backendId,
        bank: currentBank
      } as Payment);
    }
    this.availableBankConnections = connections;
  }

  /** Beim Wechsel auf Bankeinzug: bereits vorhandene bzw. einzige Bankverbindung im Dropdown vorbelegen. */
  private prepareBankSelection(): void {
    const control = this.paymentEditForm.get('bankSelection');
    let preselect = '';
    if (this.rawIban) {
      // Die Abo-IBAN ist über buildAvailableBankConnections garantiert in der Liste enthalten.
      preselect = this.rawIban;
    } else if (this.availableBankConnections.length === 1) {
      preselect = this.availableBankConnections[0].bank?.swiftIBAN;
    }
    control.setValue(preselect, {emitEvent: false});
  }

  setValidatorsAtInvoiceEmail(value: boolean) {
    if (value === true) {
      this.paymentEditForm.get('invoiceEmail').setValidators([Validators.email, Validators.required]);
      this.needInvoiceMailAddress = true;
    } else {
      this.paymentEditForm.get('invoiceEmail').clearValidators();
      this.paymentEditForm.get('invoiceEmail').reset();
      this.needInvoiceMailAddress = false;
    }
  }

  onSendPayment(): void {
    if (this.paymentEditForm.valid) {
      this.isSaving = true;
      this.isLoadingSub = true;

      const payment = this.paymentEditForm.getRawValue();
      const bankSelection = payment.bankSelection;
      delete payment.bankSelection;

      if (this.needIbanBic) {
        // Gewählte (vorhandene) Bankverbindung des Nutzers: volle IBAN/BIC der Auswahl übernehmen.
        const selected = this.availableBankConnections.find(connection => connection.bank?.swiftIBAN === bankSelection);
        payment.bank = {
          ...payment.bank,
          swiftIBAN: selected?.bank?.swiftIBAN,
          swiftBIC: selected?.bank?.swiftBIC,
        };
      }

      payment.paymentType.paymentFrequency = payment.paymentType.paymentFrequency.key;
      payment.paymentType.paymentMethod = payment.paymentType.paymentMethod.paymentMethod;

      this.hupSubscriptionsService.sendSubPaymentEdit(this.sub.backendId, payment.backendId, payment).subscribe({
        next: (res) => {
          this.navigationService.navigateTo(NavigationCommand.PAYMENT);
        },
        error: (error) => {
          const messages = error.error.validationErrors? error.error.validationErrors : error.error
          this.errorService.handleAPIError([this.paymentEditForm], messages, this.errorsArr);
          this.isLoadingSub = false;
          this.isSaving = false;
        },
        complete: () => {
          this.isLoadingSub = false;
          this.isSaving = false;
        }
      });
    }
  }

  compareBaseData(baseData1: BaseData, baseData2: BaseData): boolean {
    return baseData1.key === baseData2.key;
  }

  compareFormOfPayment(formOfPayment1: FormOfPayment, formOfPayment2: FormOfPayment): boolean {
    return formOfPayment1.paymentMethod === formOfPayment2.paymentMethod;
  }
}
